import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, List, ListItem, ListItemIcon,
  ListItemText, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import {
  EventAvailable as VisitIcon, School as TrainingIcon, Description as DocumentIcon,
  LocalShipping as DeliveryIcon, TaskAlt as AckIcon, WorkspacePremium as ConstanciaIcon,
  EventBusy as ExpirationIcon, Schedule as EstimatedIcon,
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import { clientEntityStatus } from '../../utils/clientDeliveries';

const ICONS = {
  VISITA: <VisitIcon color="primary" />,
  CAPACITACION: <TrainingIcon color="secondary" />,
  DOCUMENTO: <DocumentIcon color="action" />,
  ENTREGA: <DeliveryIcon color="warning" />,
  ACUSE: <AckIcon color="success" />,
  CONSTANCIA: <ConstanciaIcon color="success" />,
  VENCIMIENTO: <ExpirationIcon color="error" />,
  ENTREGA_ESTIMADA: <EstimatedIcon color="info" />,
};

const VISIT_LABEL = {
  PENDIENTE: 'Por programar',
  PROGRAMADA: 'Programada',
  EN_PROGRESO: 'En curso',
  COMPLETADA: 'Realizada',
};

// LocalDate ("2026-10-20") se lee a mediodía para que no se recorra un día por la zona horaria.
const toDate = (value) => (value ? new Date(value.length === 10 ? `${value}T12:00:00` : value) : null);
const formatDate = (value) => (value ? toDate(value).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const formatDateTime = (value) => {
  const d = toDate(value);
  if (!d) return '—';
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
  return d.toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit', month: 'short' })
    + (hasTime ? ` · ${d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}` : '');
};

const StatCard = ({ value, label, hint, color }) => (
  <Card sx={{ height: '100%', borderTop: `4px solid ${color}` }}>
    <CardContent>
      <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </CardContent>
  </Card>
);

const EventList = ({ items, empty }) => (items.length === 0
  ? <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{empty}</Typography>
  : (
    <List dense>
      {items.map((item, i) => (
        <ListItem key={`${item.type}-${item.date}-${i}`} disableGutters>
          <ListItemIcon sx={{ minWidth: 40 }}>{ICONS[item.type]}</ListItemIcon>
          <ListItemText
            primary={item.title}
            secondary={[formatDateTime(item.date), item.entityName, item.enrolled != null ? `${item.enrolled} inscrito(s)` : null]
              .filter(Boolean).join(' · ')}
          />
        </ListItem>
      ))}
    </List>
  ));

/**
 * Pestaña del perfil del INVITADO: avance real de su servicio (entes, esta semana, próximas
 * fechas y documentos por vencer), servido por GET /api/guest/service-progress.
 */
const AvanceMiServicio = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/guest/service-progress')
      .then((resp) => setData(resp.data))
      .catch(() => setError('No se pudo cargar el avance de tu servicio. Intenta de nuevo en unos minutos.'));
  }, []);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  const { summary, entities, thisWeek, upcoming, expiredDocuments, expiringDocuments } = data;

  if (entities.length === 0) {
    return (
      <Alert severity="info">
        Aún no hay un servicio en curso para tu empresa. Cuando aceptes una cotización y se programe tu visita,
        aquí verás el avance de cada inmueble, tus próximas fechas y tus documentos.
      </Alert>
    );
  }

  const next = summary.next;

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard value={`${summary.entitiesVisited}/${summary.entitiesTotal}`} label="Visitas realizadas"
          hint="Inmuebles ya inspeccionados" color="#1976d2" />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard value={`${summary.entitiesDelivered}/${summary.entitiesTotal}`} label="Programas entregados"
          hint={summary.pendingDeliveries > 0 ? `${summary.pendingDeliveries} esperando tu confirmación` : 'Documentos recibidos'}
          color={summary.pendingDeliveries > 0 ? '#ed6c02' : '#2e7d32'} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard value={next ? formatDate(next.date.substring(0, 10)) : '—'} label="Próxima fecha"
          hint={next ? `${next.title}${next.entityName ? ` · ${next.entityName}` : ''}` : 'Sin fechas próximas'} color="#7b1fa2" />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <StatCard value={summary.documentsExpired + summary.documentsExpiringSoon} label="Documentos por renovar"
          hint={`${summary.documentsExpired} vencido(s) · ${summary.documentsExpiringSoon} por vencer en ${data.upcomingDays} días`}
          color={summary.documentsExpired > 0 ? '#d32f2f' : '#0288d1'} />
      </Grid>

      {summary.pendingDeliveries > 0 && (
        <Grid size={{ xs: 12 }}>
          <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => navigate('/perfil-riesgo/mis-documentos')}>Ver documentos</Button>}>
            Tienes documentos listos para descargar y confirmar de recibido.
          </Alert>
        </Grid>
      )}

      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Mis inmuebles</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Inmueble</TableCell>
                  <TableCell>Orden</TableCell>
                  <TableCell>Visita</TableCell>
                  <TableCell>Fecha de visita</TableCell>
                  <TableCell>Entrega estimada</TableCell>
                  <TableCell>Documentos</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entities.map((e) => {
                  const status = clientEntityStatus(e);
                  return (
                    <TableRow key={e.detailId}>
                      <TableCell sx={{ fontWeight: 600 }}>{e.entityName}</TableCell>
                      <TableCell>{e.orderNumber}</TableCell>
                      <TableCell>{VISIT_LABEL[e.visitStatus] || e.visitStatus}</TableCell>
                      <TableCell>{formatDate(e.scheduledDate)}{e.scheduledTimeStart ? ` · ${e.scheduledTimeStart.substring(0, 5)}` : ''}</TableCell>
                      <TableCell>{formatDate(e.estimatedCompletionDate)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip size="small" label={status.label} color={status.color} variant={status.color === 'default' ? 'outlined' : 'filled'} />
                          {(e.stage === 'LIBERADA' || e.stage === 'ENTREGADA') && (
                            <Button size="small" onClick={() => navigate('/perfil-riesgo/mis-documentos')} sx={{ textTransform: 'none' }}>
                              Ver mis documentos
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Esta semana</Typography>
          <Typography variant="caption" color="text.secondary">Desde el {formatDate(data.weekStart)}</Typography>
          <EventList items={thisWeek} empty="Sin movimientos en los últimos 7 días." />
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Paper sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Próximas fechas</Typography>
          <Typography variant="caption" color="text.secondary">Siguientes {data.upcomingDays} días</Typography>
          <EventList items={upcoming} empty="No hay visitas, capacitaciones ni vencimientos próximos." />
        </Paper>
      </Grid>

      {(expiredDocuments.length > 0 || expiringDocuments.length > 0) && (
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Documentos por renovar</Typography>
              <Button size="small" variant="outlined" onClick={() => navigate('/perfil-riesgo/subir-archivos')}>Subir documentos</Button>
            </Box>
            <List dense>
              {[...expiredDocuments.map((d) => ({ ...d, expired: true })), ...expiringDocuments].map((d, i) => (
                <ListItem key={`${d.document}-${i}`} disableGutters>
                  <ListItemIcon sx={{ minWidth: 40 }}><ExpirationIcon color={d.expired ? 'error' : 'warning'} /></ListItemIcon>
                  <ListItemText primary={d.document}
                    secondary={`${d.entityName} · ${d.expired ? 'Venció' : 'Vence'} el ${formatDate(d.expirationDate)}`} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default AvanceMiServicio;
